import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAutosaveStatus } from './useAutosaveStatus';
import { useDebouncedAutosave } from './useDebouncedAutosave';
import type { SaveStatus } from '../ui/feedback';

/**
 * Callbacks the caller's {@link UseDraftAutosaveOptions.save} MUST
 * invoke to drive the draft state machine. Wire them to the
 * underlying mutation's lifecycle:
 *   - `onSuccess` when the write persisted (fire AFTER any
 *     on-success cache patch).
 *   - `onSettled` exactly once when the write finished, success or
 *     error (fire AFTER any rollback).
 * Both fire synchronously from within the mutation callbacks — the
 * state machine relies on that timing for its single-flight and
 * follow-up guarantees.
 */
export interface DraftSaveCallbacks {
  readonly onSuccess: () => void;
  readonly onSettled: () => void;
}

export interface UseDraftAutosaveOptions<TValue> {
  /**
   * The latest server-persisted value, recomputed by the caller each
   * render. Compared by VALUE (via {@link UseDraftAutosaveOptions.isEqual}),
   * so a fresh reference carrying equal content is safely ignored.
   * Seeds the draft on mount and re-anchors it on server change.
   */
  readonly serverSnapshot: TValue;
  /** Value equality — drives the dirty check, reseed, and intent re-check. */
  readonly isEqual: (a: TValue, b: TValue) => boolean;
  /** Debounce window (ms) for {@link DraftAutosaveApi.setDraft}. */
  readonly debounceMs: number;
  /** When true, edits are dropped and no save fires. */
  readonly isReadOnly: boolean;
  /** Underlying mutation's in-flight flag — feeds the save indicator. */
  readonly isPending: boolean;
  /** Underlying mutation's error flag — feeds the save indicator. */
  readonly isError: boolean;
  /**
   * Persist `value`. The caller builds the payload, fires the
   * mutation, applies any optimistic / on-success cache patch and
   * rollback, and MUST invoke `callbacks.onSuccess` (on success) and
   * `callbacks.onSettled` (always, last). Kept callback-style rather
   * than promise-returning so the mutation's synchronous callbacks
   * drive the state machine without a microtask hop.
   */
  readonly save: (value: TValue, callbacks: DraftSaveCallbacks) => void;
  /** "Saved" flash duration — forwarded to {@link useAutosaveStatus}. */
  readonly savedDurationMs?: number;
}

export interface DraftAutosaveApi<TValue> {
  /** Current in-flight draft value. */
  readonly value: TValue;
  /**
   * Update the draft with a DEBOUNCED save. Accepts a value or an
   * updater `(prev) => next`. No-op when read-only.
   */
  readonly setDraft: (next: TValue | ((prev: TValue) => TValue)) => void;
  /**
   * Update the draft and save IMMEDIATELY (no debounce). Use for
   * discrete, deliberate commits. No-op when read-only.
   */
  readonly commitNow: (value: TValue) => void;
  /**
   * Persist any dirty draft now, joining an in-flight save and
   * chaining a follow-up if the draft diverged. Resolves after
   * settlement.
   */
  readonly flush: () => Promise<void>;
  /** Idle / Saving / Saved (flash) / Error — feed into `SaveIndicator`. */
  readonly saveStatus: SaveStatus;
}

/** Minimal mutable-ref shape — avoids React major-version type drift. */
interface Slot<T> {
  current: T;
}

/**
 * Mutable state shared between the hook and its module-level step
 * helpers. Bundling the refs keeps each helper's signature short and
 * keeps the hook body under the cognitive-complexity budget.
 */
interface DraftMachine<TValue> {
  readonly draftRef: Slot<TValue>;
  readonly persistedRef: Slot<TValue>;
  readonly isSavingRef: Slot<boolean>;
  readonly pendingSavePromiseRef: Slot<Promise<void> | null>;
  readonly lastIntentRef: Slot<TValue>;
  readonly isEqualRef: Slot<(a: TValue, b: TValue) => boolean>;
  readonly dispatchRef: Slot<(value: TValue) => Promise<void>>;
  readonly setPersisted: (value: TValue) => void;
  readonly setDraftState: (value: TValue) => void;
  readonly markSaved: () => void;
}

/**
 * Shared debounced-field autosave state machine.
 *
 * Owns the parts that every "typed/selected value that autosaves to
 * one PATCH" surface needs and that were previously hand-rolled per
 * hook: draft vs persisted tracking, a debounced save path, an
 * immediate save path, a single-flight guard, `flush()` (join +
 * follow-up), the reseed handshake (clean drafts resync to fresh
 * server values, dirty drafts keep the user's edits), the
 * async-intent re-check (a save whose value was superseded while in
 * flight does not anchor or flash "Saved"), and the Saving/Saved/
 * Error indicator.
 *
 * Persistence specifics — payload shape, mutation, optimistic vs
 * on-success cache patch, rollback, close-audit-gate registration —
 * live entirely in the caller's {@link UseDraftAutosaveOptions.save}.
 * The primitive is mutation-agnostic and value-shape-agnostic (the
 * generic `TValue` + `isEqual` cover scalar fields and keyed maps
 * alike), so a single implementation serves every consumer.
 *
 * Consumers: `useSubQuestionValueDraft` (scalar data-collection
 * value) and `useCommentDraft` (comment map). Any future autosaved
 * field plugs in the same way.
 */
export function useDraftAutosave<TValue>(
  options: UseDraftAutosaveOptions<TValue>,
): DraftAutosaveApi<TValue> {
  const {
    serverSnapshot,
    isEqual,
    debounceMs,
    isReadOnly,
    isPending,
    isError,
    save,
    savedDurationMs,
  } = options;

  const [draft, setDraftState] = useState<TValue>(serverSnapshot);
  // `persisted` is state (so useDebouncedAutosave's snapshot compare
  // stays reactive) AND mirrored to a ref (so dispatch/flush read the
  // latest synchronously inside callbacks).
  const [persisted, setPersisted] = useState<TValue>(serverSnapshot);

  const { saveStatus, markSaved } = useAutosaveStatus({
    isPending,
    isError,
    ...(savedDurationMs !== undefined ? { savedDurationMs } : {}),
  });

  const draftRef = useRef<TValue>(serverSnapshot);
  const persistedRef = useRef<TValue>(serverSnapshot);
  const isSavingRef = useRef<boolean>(false);
  const pendingSavePromiseRef = useRef<Promise<void> | null>(null);
  const lastIntentRef = useRef<TValue>(serverSnapshot);
  const isEqualRef = useRef(isEqual);
  const dispatchRef = useRef<(value: TValue) => Promise<void>>(() => Promise.resolve());

  useEffect(() => {
    isEqualRef.current = isEqual;
  }, [isEqual]);

  const machine = useMemo<DraftMachine<TValue>>(
    () => ({
      draftRef,
      persistedRef,
      isSavingRef,
      pendingSavePromiseRef,
      lastIntentRef,
      isEqualRef,
      dispatchRef,
      setPersisted,
      setDraftState,
      markSaved,
    }),
    [markSaved],
  );

  const dispatch = useCallback(
    (value: TValue): Promise<void> => runDispatch(machine, value, isReadOnly, save),
    [machine, isReadOnly, save],
  );

  // Stable holder so the debounced onSave and the onSettled follow-up
  // always call the latest dispatch without re-scheduling the timer.
  useEffect(() => {
    dispatchRef.current = dispatch;
  }, [dispatch]);

  useEffect(() => {
    runReseed(machine, serverSnapshot);
  }, [machine, serverSnapshot]);

  useDebouncedAutosave<TValue>({
    value: draft,
    delayMs: debounceMs,
    isEqual,
    persistedSnapshot: persisted,
    onSave: (next) => {
      void dispatchRef.current(next);
    },
  });

  const setDraft = useCallback(
    (next: TValue | ((prev: TValue) => TValue)): void => {
      if (isReadOnly) return;
      // Resolve updaters against draftRef (authoritative) rather than
      // passing a functional updater to setState — keeps the setState
      // updater pure (no ref mutation inside it) per StrictMode rules.
      const resolved = isUpdater(next) ? next(draftRef.current) : next;
      draftRef.current = resolved;
      setDraftState(resolved);
    },
    [isReadOnly],
  );

  const commitNow = useCallback(
    (value: TValue): void => {
      if (isReadOnly) return;
      draftRef.current = value;
      setDraftState(value);
      void dispatchRef.current(value);
    },
    [isReadOnly],
  );

  const flush = useCallback((): Promise<void> => runFlush(machine), [machine]);

  return useMemo(
    () => ({ value: draft, setDraft, commitNow, flush, saveStatus }),
    [draft, setDraft, commitNow, flush, saveStatus],
  );
}

/** Narrow the setDraft argument without an unsafe cast at the call site. */
function isUpdater<TValue>(
  next: TValue | ((prev: TValue) => TValue),
): next is (prev: TValue) => TValue {
  return typeof next === 'function';
}

/**
 * One save attempt. Guards (read-only, clean, single-flight), records
 * the latest intent, delegates persistence to `save`, and wires the
 * intent re-check + follow-up into the caller's callbacks.
 */
function runDispatch<TValue>(
  machine: DraftMachine<TValue>,
  value: TValue,
  isReadOnly: boolean,
  save: (value: TValue, callbacks: DraftSaveCallbacks) => void,
): Promise<void> {
  const equals = machine.isEqualRef.current;
  if (isReadOnly) return Promise.resolve();
  if (equals(value, machine.persistedRef.current)) return Promise.resolve();
  // Record latest intent BEFORE the single-flight guard so a value
  // committed while a save is in flight updates it — a stale
  // completion then fails the onSuccess re-check below.
  machine.lastIntentRef.current = value;
  if (machine.isSavingRef.current) {
    return machine.pendingSavePromiseRef.current ?? Promise.resolve();
  }

  machine.isSavingRef.current = true;
  let resolveSettled: () => void = () => undefined;
  const promise = new Promise<void>((resolve) => {
    resolveSettled = resolve;
  });
  machine.pendingSavePromiseRef.current = promise;

  save(value, {
    onSuccess: () => {
      // Only anchor + flash when the value we saved is STILL the
      // latest committed intent (async-intent rule).
      if (equals(machine.lastIntentRef.current, value)) {
        machine.persistedRef.current = value;
        machine.setPersisted(value);
        machine.markSaved();
      }
    },
    onSettled: () => {
      machine.isSavingRef.current = false;
      resolveSettled();
      // Follow-up ONLY when the draft moved to a NEW value while this
      // save was in flight (compared to the attempted value, not the
      // persisted anchor — a failed save never anchors, so comparing
      // to persisted would re-dispatch forever).
      if (!equals(machine.draftRef.current, value)) {
        void machine.dispatchRef.current(machine.draftRef.current);
      }
    },
  });

  return promise.finally(() => {
    if (machine.pendingSavePromiseRef.current === promise) {
      machine.pendingSavePromiseRef.current = null;
    }
  });
}

/**
 * Persist a dirty draft now: join an in-flight save and chain a
 * follow-up when the draft diverged, else dispatch directly.
 */
function runFlush<TValue>(machine: DraftMachine<TValue>): Promise<void> {
  const equals = machine.isEqualRef.current;
  if (equals(machine.draftRef.current, machine.persistedRef.current)) {
    return Promise.resolve();
  }
  const pending = machine.pendingSavePromiseRef.current;
  if (machine.isSavingRef.current && pending) {
    return pending.then((): Promise<void> | void => {
      if (!equals(machine.draftRef.current, machine.persistedRef.current)) {
        return machine.dispatchRef.current(machine.draftRef.current);
      }
    });
  }
  return machine.dispatchRef.current(machine.draftRef.current);
}

/**
 * Re-anchor on a server-value change. Clean drafts resync to the
 * fresh value; dirty drafts keep the user's unsaved edits.
 */
function runReseed<TValue>(machine: DraftMachine<TValue>, serverSnapshot: TValue): void {
  const equals = machine.isEqualRef.current;
  // Identity-only churn (a background refetch handing back an equal
  // snapshot with a fresh reference) MUST NOT set state — doing so
  // re-renders, produces another new reference, and loops forever.
  // Gate on VALUE equality, not reference.
  if (equals(machine.persistedRef.current, serverSnapshot)) return;

  const wasClean = equals(machine.draftRef.current, machine.persistedRef.current);
  machine.persistedRef.current = serverSnapshot;
  machine.setPersisted(serverSnapshot);
  if (wasClean && !equals(machine.draftRef.current, serverSnapshot)) {
    machine.draftRef.current = serverSnapshot;
    machine.setDraftState(serverSnapshot);
  }
}
